// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

// ADR-025 (revised): token lives in memory with a localStorage fallback
// (see api/apiBase.ts); headers are read per-call, not snapshotted.
import { getAccessToken } from '@api/apiBase';

export type OptionalFetchKind =
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'server'
  | 'network'
  | 'unknown';

export type OptionalFetchResult<T> =
  | { ok: true; status: number; data: T }
  | { ok: false; status: number | null; kind: OptionalFetchKind; message: string };

export function buildStoredAuthHeaders(): Record<string, string> {
  if (typeof window === 'undefined') {
    return {};
  }
  const accessToken = getAccessToken();
  return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
}

/** QUAL T3/P2-45：注入式 helper（fetchImpl 缝）的制裁认证传输——
 * 裸 fetch 在本模块集中，调用面只传函数引用。 */
export async function authedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  return fetch(input, {
    ...init,
    headers: { ...(init?.headers || {}), ...buildStoredAuthHeaders() },
  });
}

export async function fetchOptionalJson<T>(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<OptionalFetchResult<T>> {
  try {
    const response = await fetch(input, init);
    const status = response.status;
    const text = await response.text();

    if (response.ok) {
      if (!text) {
        return { ok: true, status, data: {} as T };
      }
      try {
        return { ok: true, status, data: JSON.parse(text) as T };
      } catch {
        return { ok: true, status, data: {} as T };
      }
    }

    if (status === 401) {
      return { ok: false, status, kind: 'unauthorized', message: `Unauthorized (${status})` };
    }
    if (status === 403) {
      return { ok: false, status, kind: 'forbidden', message: `Forbidden (${status})` };
    }
    if (status === 404) {
      return { ok: false, status, kind: 'not_found', message: `Not Found (${status})` };
    }
    if (status >= 500) {
      return { ok: false, status, kind: 'server', message: `Server error (${status})` };
    }
    return { ok: false, status, kind: 'unknown', message: `Request failed (${status})` };
  } catch (error) {
    return {
      ok: false,
      status: null,
      kind: 'network',
      message: error instanceof Error ? error.message : 'Network error',
    };
  }
}

export type RuntimeMountStatus =
  | 'mounted'
  | 'disabled'
  | 'unauthorized'
  | 'not_found'
  | 'network'
  | 'unavailable';

export interface RuntimeMountView {
  ready: boolean;
  status: RuntimeMountStatus;
  message: string;
  path?: string;
}

export function runtimeMountViewFromFetchFailure(
  result: OptionalFetchResult<unknown>
): RuntimeMountView {
  if (result.ok) {
    return { ready: true, status: 'mounted', message: 'Runtime mounted.' };
  }
  if (result.kind === 'unauthorized' || result.kind === 'forbidden') {
    return { ready: false, status: 'unauthorized', message: result.message };
  }
  if (result.kind === 'not_found') {
    return { ready: false, status: 'not_found', message: result.message };
  }
  if (result.kind === 'network') {
    return { ready: false, status: 'network', message: result.message };
  }
  return { ready: false, status: 'unavailable', message: result.message };
}

export function runtimeMountViewFromCapability(entry?: {
  enabled?: boolean;
  mounted?: boolean;
  runtime_available?: boolean;
  status?: string;
  path?: string;
  detail?: string;
  async_reason?: string | null;
}): RuntimeMountView {
  if (!entry) {
    return { ready: false, status: 'unavailable', message: 'Runtime capability is unavailable.' };
  }
  if (entry.enabled === false) {
    return {
      ready: false,
      status: 'disabled',
      message: entry.detail || entry.async_reason || 'Runtime disabled.',
      path: entry.path,
    };
  }
  if (entry.mounted || entry.runtime_available) {
    return {
      ready: true,
      status: 'mounted',
      message: entry.detail || 'Runtime mounted.',
      path: entry.path,
    };
  }
  const status = entry.status === 'disabled' ? 'disabled' : 'unavailable';
  return {
    ready: false,
    status,
    message: entry.detail || entry.async_reason || entry.status || 'Runtime unavailable.',
    path: entry.path,
  };
}
